/**
 * Styles for the visual regression section of the HTML report.
 *
 * Kept out of `html-css.ts` deliberately — that file is already past the
 * 500-line ceiling in Constitution Principle II, so growing it further would
 * make a bad situation worse.
 *
 * The comparison modes lean on two layered images of identical size: the
 * baseline sits in normal flow and sets the box, the candidate is absolutely
 * positioned on top of it, and a single slider drives either a `clip-path`
 * wipe or an opacity blend. No canvas, no measurement, nothing to keep in sync
 * on resize.
 */

export const CSS_VISUAL = `
.vis-header{font-size:11px;font-weight:700;color:var(--fg-1);text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px;display:flex;align-items:center;gap:6px}
.vis-header svg{opacity:.6}
.vis-count{font-weight:400;color:var(--fg-2);font-size:10px}
.vis-count-fail{color:#ef4444}
.vis-list{display:flex;flex-direction:column;gap:20px}
.vis-card{border:1px solid var(--bd);border-radius:10px;padding:14px;background:var(--bg-2);min-width:0}
.vis-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:10px}
.vis-name{font-size:12px;font-weight:600;color:var(--fg-1);word-break:break-all}
.vis-badge{font-size:10px;font-weight:600;padding:2px 8px;border-radius:999px;text-transform:uppercase;letter-spacing:.04em;white-space:nowrap}
.vis-badge.vb-passed{background:rgba(3,183,156,0.14);color:#2dd4a8}
.vis-badge.vb-failed{background:rgba(239,68,68,0.14);color:#ef4444}
.vis-badge.vb-new{background:rgba(59,130,246,0.14);color:#60a5fa}
.vis-badge.vb-updated{background:rgba(234,179,8,0.14);color:#eab308}
.vis-badge.vb-dom{background:rgba(232,121,249,0.14);color:#e879f9}
.vis-metrics{font-size:11px;color:var(--fg-2);margin-left:auto;white-space:nowrap;font-variant-numeric:tabular-nums}
.vis-note{font-size:11px;color:var(--fg-2);font-style:italic;padding:6px 0}

/* Side-by-side: wraps to a single column when the drawer is narrow. */
.vis-pair{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px}
.vis-pane-label{font-size:10px;font-weight:600;color:var(--fg-2);text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px}

/* Layered comparison surface shared by the slider and onion-skin modes. */
.vis-stack{position:relative;line-height:0;border:1px solid var(--bd);border-radius:8px;overflow:hidden;background:var(--bg)}
.vis-stack img{display:block;width:100%;height:auto}
.vis-stack .vis-top{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain}
.vis-handle{position:absolute;top:0;bottom:0;width:2px;background:#03b79c;pointer-events:none;box-shadow:0 0 0 1px rgba(0,0,0,0.35)}
.vis-range{width:100%;margin-top:10px;accent-color:#03b79c;cursor:ew-resize}
.vis-range-row{display:flex;align-items:center;gap:10px;font-size:10px;color:var(--fg-2);text-transform:uppercase;letter-spacing:.04em}
.vis-range-row .vis-range{margin-top:0}

/* A checkerboard behind the diff so fully transparent regions stay legible. */
.vis-diff-wrap{background-image:linear-gradient(45deg,var(--bd-xs) 25%,transparent 25%),linear-gradient(-45deg,var(--bd-xs) 25%,transparent 25%),linear-gradient(45deg,transparent 75%,var(--bd-xs) 75%),linear-gradient(-45deg,transparent 75%,var(--bd-xs) 75%);background-size:16px 16px;background-position:0 0,0 8px,8px -8px,-8px 0;border-radius:8px}
`;

/**
 * Styles for the Elements pane — the DOM comparison.
 *
 * The overlay mirrors the platform's existing region overlay: an absolutely
 * positioned layer over a `position:relative` image, with each box placed in
 * percentages so nothing needs measuring or re-measuring on resize.
 */
export const CSS_DOM = `
.dom-figure{position:relative;line-height:0;border:1px solid var(--bd);border-radius:8px;overflow:hidden;margin-bottom:12px}
.dom-img{display:block;width:100%;height:auto}
.dom-ov{position:absolute;top:0;left:0;right:0;bottom:0;pointer-events:none}
.dom-box{position:absolute;border:2px solid #03b79c;border-radius:2px;opacity:.55;transition:opacity .12s,box-shadow .12s}
.dom-box.hi{opacity:1;box-shadow:0 0 0 2px rgba(0,0,0,.45),0 0 12px rgba(255,255,255,.25)}

.dom-summary{font-size:11px;color:var(--fg-2);margin-bottom:10px}
.dom-summary strong{color:var(--fg-1);font-variant-numeric:tabular-nums}
.dom-truncated{display:inline-block;margin-left:6px;padding:1px 6px;border-radius:999px;background:rgba(234,179,8,.14);color:#eab308;font-size:10px;font-weight:600}

.dom-list{display:flex;flex-direction:column;gap:2px}
.dom-group-label{font-size:10px;font-weight:600;color:var(--fg-2);text-transform:uppercase;letter-spacing:.04em;margin:12px 0 4px}
.dom-row{display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:6px;cursor:default;flex-wrap:wrap}
.dom-row:hover,.dom-row.hi{background:var(--bd-xs)}
.dom-swatch{width:8px;height:8px;border-radius:2px;flex:none}
.dom-label{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;color:var(--fg-1);word-break:break-all}
.dom-kind{font-size:9px;font-weight:600;padding:1px 6px;border-radius:999px;text-transform:uppercase;letter-spacing:.04em;background:var(--bd-xs);color:var(--fg-2)}
.dom-kind.dk-restyled{background:rgba(232,121,249,.14);color:#e879f9}
.dom-kind.dk-text{background:rgba(96,165,250,.14);color:#60a5fa}
.dom-kind.dk-attrs{background:rgba(251,146,60,.14);color:#fb923c}
.dom-kind.dk-added{background:rgba(3,183,156,.14);color:#2dd4a8}
.dom-kind.dk-removed{background:rgba(239,68,68,.14);color:#ef4444}
.dom-area{margin-left:auto;font-size:10px;color:var(--fg-2);font-variant-numeric:tabular-nums}

.dom-detail{padding:0 8px 8px 24px;display:flex;flex-direction:column;gap:3px}
.dom-prop{display:flex;align-items:baseline;gap:8px;font-size:10px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;flex-wrap:wrap}
.dom-prop-name{color:var(--fg-2);min-width:130px}
.dom-prop-attr .dom-prop-name{color:#fb923c;font-weight:600}
.dom-from{color:#ef4444;word-break:break-all}
.dom-arrow{color:var(--fg-2)}
.dom-to{color:#2dd4a8;word-break:break-all}
`;
